/**
 * ExportManager.js
 * Handles exporting the scene to various 3D file formats
 * Supports GLTF and STL formats with proper scene preparation
 */

import EventEmitter from '../core/EventEmitter.js';
import { SHAPE_TYPES } from '../utils/Constants.js';
import { getTimestamp, downloadFile } from '../utils/Helpers.js';

// Maximum imported object count to prevent resource exhaustion
const MAX_IMPORT_OBJECTS = 10000;

class ExportManager extends EventEmitter {
    /**
     * @param {THREE.Scene} scene - The Three.js scene
     * @param {SWK} swkInstance - Main SWK instance
     * @param {Object} config - Configuration object
     */
    constructor(scene, swkInstance, config) {
        super();
        
        this.scene = scene;
        this.swk = swkInstance;
        this.config = config;
        
        // Check if exporters are available
        this.hasGLTFExporter = typeof THREE !== 'undefined' && THREE.GLTFExporter;
        this.hasSTLExporter = typeof THREE !== 'undefined' && THREE.STLExporter;
        
        if (!this.hasGLTFExporter) {
            console.warn('ExportManager: GLTFExporter not found. GLTF export will not be available.');
        }
        
        if (!this.hasSTLExporter) {
            console.warn('ExportManager: STLExporter not found. STL export will not be available.');
        }
    }

    /**
     * Prepare scene for export (filter out non-exportable objects)
     * @returns {Array} Array of exportable objects (includes group containers)
     */
    prepareSceneForExport() {
        const exportableObjects = [];
        
        this.scene.children.forEach(child => {
            const ud = child.userData || {};
            // Export user-created objects. Group containers are included so
            // their children are exported with hierarchy and transforms (F05)
            if (ud.isUserObject || ud.isGroupResult) {
                exportableObjects.push(child);
            }
        });
        
        return exportableObjects;
    }

    /**
     * Create a temporary scene with only exportable objects
     * @returns {THREE.Scene} Temporary scene for export
     */
    createExportScene() {
        const exportScene = new THREE.Scene();
        const objects = this.prepareSceneForExport();
        
        // Clone objects to temporary scene
        objects.forEach(obj => {
            const clone = obj.clone(true);
            exportScene.add(clone);
        });
        
        // Ensure world matrices reflect the latest transforms
        exportScene.updateMatrixWorld(true);
        
        return exportScene;
    }

    /**
     * Clone an object baking its current world transform into the clone root,
     * so selected children keep their intended world placement when detached
     * from transformed parents (F11)
     */
    cloneWithWorldTransform(obj) {
        const clone = obj.clone(true);
        obj.updateWorldMatrix(true, false);
        clone.matrix.copy(obj.matrixWorld);
        clone.matrix.decompose(clone.position, clone.quaternion, clone.scale);
        clone.updateMatrix();
        return clone;
    }
    
    /**
     * Export a explicit list of objects as GLTF/GLB (stateless - does not
     * touch shared manager state, F10)
     * @param {Array<THREE.Object3D>} objects - Objects to export
     * @param {Object} options - Export options
     * @returns {Promise<boolean>} Success status
     */
    async exportObjectsGLTF(objects, options = {}) {
        if (!this.hasGLTFExporter) {
            console.error('GLTFExporter is not available');
            this.emit('exportError', { format: 'gltf', error: 'GLTFExporter not found' });
            return false;
        }

        const binary = options.binary !== undefined ? options.binary : false;
        const filename = options.filename || `scene-${getTimestamp()}`;
        const extension = binary ? 'glb' : 'gltf';
        
        try {
            this.emit('exportStart', { format: extension });
            
            if (!objects || objects.length === 0) {
                console.warn('No objects to export');
                this.emit('exportError', { format: extension, error: 'No objects to export' });
                return false;
            }
            
            // Build a temporary scene from the given objects
            const exportScene = new THREE.Scene();
            objects.forEach(obj => {
                exportScene.add(obj.clone(true));
            });
            exportScene.updateMatrixWorld(true);
            
            // Create exporter
            const exporter = new THREE.GLTFExporter();
            
            // Export options
            const exportOptions = {
                binary: binary,
                embedImages: true,
                truncateDrawRange: false,
                onlyVisible: true,
                maxTextureSize: 4096
            };
            
            // Export - settle the promise exactly once with a guaranteed
            // error path and binary output validation (F04)
            return new Promise((resolve) => {
                let settled = false;
                
                const fail = (error) => {
                    if (settled) return;
                    settled = true;
                    console.error('GLTF export failed:', error);
                    this.emit('exportError', { format: extension, error: error.message });
                    resolve(false);
                };
                
                const succeed = (result) => {
                    if (settled) return;
                    try {
                        if (binary) {
                            // GLB - validate binary output before download (F04)
                            if (!(result instanceof ArrayBuffer) || result.byteLength < 12) {
                                throw new Error('GLTFExporter returned invalid binary output');
                            }
                            const magic = new Uint8Array(result, 0, 4);
                            const isGlb = magic[0] === 0x67 && magic[1] === 0x6c &&
                                          magic[2] === 0x54 && magic[3] === 0x46; // 'glTF'
                            if (!isGlb) {
                                throw new Error('GLB output has invalid magic header');
                            }
                            const blob = new Blob([result], { type: 'application/octet-stream' });
                            downloadFile(blob, `${filename}.${extension}`);
                        } else {
                            // GLTF - JSON format
                            if (!result || typeof result !== 'object') {
                                throw new Error('GLTFExporter returned invalid JSON output');
                            }
                            const output = JSON.stringify(result, null, 2);
                            const blob = new Blob([output], { type: 'application/json' });
                            downloadFile(blob, `${filename}.${extension}`);
                        }
                        
                        console.log(`Exported ${exportScene.children.length} objects as ${extension.toUpperCase()}`);
                        this.emit('exportComplete', { 
                            format: extension, 
                            filename: `${filename}.${extension}`,
                            objectCount: exportScene.children.length
                        });
                        settled = true;
                        resolve(true);
                    } catch (error) {
                        fail(error);
                    }
                };
                
                try {
                    // r128 GLTFExporter signature: parse(input, onDone, options)
                    exporter.parse(exportScene, succeed, exportOptions);
                } catch (error) {
                    fail(error);
                }
            });
            
        } catch (error) {
            console.error('GLTF export error:', error);
            this.emit('exportError', { format: extension, error: error.message });
            return false;
        }
    }

    /**
     * Export scene as GLTF/GLB format
     * @param {Object} options - Export options
     * @param {boolean} options.binary - Export as GLB (binary) instead of GLTF
     * @param {string} options.filename - Custom filename (without extension)
     * @returns {Promise<boolean>} Success status
     */
    async exportGLTF(options = {}) {
        return this.exportObjectsGLTF(this.prepareSceneForExport(), options);
    }
    
    /**
     * Export a explicit list of objects as STL (stateless, F10)
     * @param {Array<THREE.Object3D>} objects - Objects to export
     * @param {Object} options - Export options
     * @param {boolean} options.binary - Export as binary STL instead of ASCII
     * @param {string} options.filename - Custom filename (without extension)
     * @returns {Promise<boolean>} Success status
     */
    async exportObjectsSTL(objects, options = {}) {
        if (!this.hasSTLExporter) {
            console.error('STLExporter is not available');
            this.emit('exportError', { format: 'stl', error: 'STLExporter not found' });
            return false;
        }

        const binary = options.binary !== undefined ? options.binary : true;
        const filename = options.filename || `scene-${getTimestamp()}`;
        
        try {
            this.emit('exportStart', { format: 'stl' });
            
            if (!objects || objects.length === 0) {
                console.warn('No objects to export');
                this.emit('exportError', { format: 'stl', error: 'No objects to export' });
                return false;
            }
            
            // Create exporter
            const exporter = new THREE.STLExporter();
            
            // STL can only export one mesh at a time or merged geometry
            // We'll create a group and export it
            const exportGroup = new THREE.Group();
            objects.forEach(obj => {
                exportGroup.add(obj.clone(true));
            });
            
            // Ensure world matrices reflect the latest transforms (F11)
            exportGroup.updateMatrixWorld(true);
            
            // Export
            const result = exporter.parse(exportGroup, { binary: binary });
            
            // Download
            if (binary) {
                // Binary STL
                const blob = new Blob([result], { type: 'application/octet-stream' });
                downloadFile(blob, `${filename}.stl`);
            } else {
                // ASCII STL
                const blob = new Blob([result], { type: 'text/plain' });
                downloadFile(blob, `${filename}.stl`);
            }
            
            console.log(`Exported ${objects.length} objects as STL (${binary ? 'binary' : 'ASCII'})`);
            this.emit('exportComplete', { 
                format: 'stl', 
                filename: `${filename}.stl`,
                objectCount: objects.length,
                binary: binary
            });
            
            return true;
            
        } catch (error) {
            console.error('STL export error:', error);
            this.emit('exportError', { format: 'stl', error: error.message });
            return false;
        }
    }

    /**
     * Export scene as STL format
     * @param {Object} options - Export options
     * @param {boolean} options.binary - Export as binary STL instead of ASCII
     * @param {string} options.filename - Custom filename (without extension)
     * @returns {Promise<boolean>} Success status
     */
    async exportSTL(options = {}) {
        return this.exportObjectsSTL(this.prepareSceneForExport(), options);
    }

    /**
     * Export selected objects only.
     * Stateless: works on a temporary clone list and never swaps shared
     * manager state, so concurrent calls cannot corrupt each other (F10)
     * @param {string} format - Export format ('gltf', 'glb', or 'stl')
     * @param {Object} options - Export options
     * @returns {Promise<boolean>} Success status
     */
    async exportSelected(format = 'gltf', options = {}) {
        const selected = this.swk.getSelectedObjects();
        
        if (selected.length === 0) {
            console.warn('No objects selected for export');
            this.emit('exportError', { format, error: 'No objects selected' });
            return false;
        }
        
        // Build stateless export list with world transforms baked in (F11)
        const exportObjects = selected.map(obj => this.cloneWithWorldTransform(obj));
        
        let result;
        if (format === 'glb') {
            result = await this.exportObjectsGLTF(exportObjects, { ...options, binary: true });
        } else if (format === 'gltf') {
            result = await this.exportObjectsGLTF(exportObjects, { ...options, binary: false });
        } else if (format === 'stl') {
            result = await this.exportObjectsSTL(exportObjects, options);
        } else {
            console.error('Unknown export format:', format);
            this.emit('exportError', { format, error: `Unknown export format: ${format}` });
            result = false;
        }
        
        return result;
    }

    /**
     * Get available export formats
     * @returns {Array<Object>} Array of available export formats
     */
    getAvailableFormats() {
        const formats = [];
        
        if (this.hasGLTFExporter) {
            formats.push(
                { format: 'gltf', name: 'GLTF (JSON)', extension: 'gltf', binary: false },
                { format: 'glb', name: 'GLB (Binary)', extension: 'glb', binary: true }
            );
        }
        
        if (this.hasSTLExporter) {
            formats.push(
                { format: 'stl', name: 'STL', extension: 'stl', binary: true }
            );
        }
        
        return formats;
    }

    /**
     * Check if a format is available
     * @param {string} format - Format to check
     * @returns {boolean} True if available
     */
    isFormatAvailable(format) {
        const formats = this.getAvailableFormats();
        return formats.some(f => f.format === format);
    }

    /**
     * Export scene as JSON format
     * @param {Object} options - Export options
     * @param {string} options.filename - Custom filename (without extension)
     * @returns {Promise<boolean>} Success status
     */
    async exportJSON(options = {}) {
        const filename = options.filename || `scene-${getTimestamp()}`;
        
        try {
            this.emit('exportStart', { format: 'json' });
            
            const data = {
                metadata: {
                    version: '1.0',
                    timestamp: Date.now(),
                    generator: 'SenangWebs Kiln'
                },
                groups: [],
                objects: []
            };
            
            // Export groups
            this.swk.groupManager.getAllGroups().forEach(group => {
                data.groups.push({
                    id: group.id,
                    name: group.name,
                    transform: {
                        position: group.container.position.toArray(),
                        rotation: group.container.quaternion.toArray(),
                        scale: group.container.scale.toArray()
                    }
                });
            });
            
            // Export objects
            this.swk.objects.forEach(obj => {
                // Skip if not user object
                if (!obj.userData.isUserObject) return;
                
                const objData = {
                    type: obj.userData.shapeType,
                    name: obj.name,
                    userData: { ...obj.userData },
                    transform: {
                        position: obj.position.toArray(),
                        rotation: obj.quaternion.toArray(),
                        scale: obj.scale.toArray()
                    },
                    // Serialize actual runtime appearance and visibility (F06)
                    visible: obj.visible,
                    material: obj.material ? {
                        color: obj.material.color.getHex(),
                        opacity: obj.material.opacity,
                        transparent: obj.material.transparent,
                        wireframe: obj.material.wireframe
                    } : null,
                    groupId: obj.userData.groupId || null
                };
                
                data.objects.push(objData);
            });
            
            // Download
            const output = JSON.stringify(data, null, 2);
            const blob = new Blob([output], { type: 'application/json' });
            downloadFile(blob, `${filename}.json`);
            
            console.log(`Exported ${data.objects.length} objects as JSON`);
            this.emit('exportComplete', { 
                format: 'json', 
                filename: `${filename}.json`,
                objectCount: data.objects.length
            });
            
            return true;
            
        } catch (error) {
            console.error('JSON export error:', error);
            this.emit('exportError', { format: 'json', error: error.message });
            return false;
        }
    }

    /**
     * Validate an import document completely before any scene mutation (F03)
     * @param {Object} data - Parsed JSON document
     * @returns {string|null} Error message or null when valid
     */
    _validateImportDocument(data) {
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            return 'Invalid JSON document';
        }
        if (!data.metadata || typeof data.metadata !== 'object') {
            return 'Missing scene metadata';
        }
        if (!Array.isArray(data.objects)) {
            return 'Missing objects array';
        }
        if (data.objects.length > MAX_IMPORT_OBJECTS) {
            return `Too many objects (limit is ${MAX_IMPORT_OBJECTS})`;
        }
        if (data.groups && !Array.isArray(data.groups)) {
            return 'Invalid groups array';
        }
        
        const validShapeTypes = Object.values(SHAPE_TYPES);
        
        for (const objData of data.objects) {
            if (!objData || typeof objData !== 'object' || Array.isArray(objData)) {
                return 'Invalid object entry';
            }
            const type = typeof objData.type === 'string' ? objData.type.toLowerCase() : null;
            if (!type || !validShapeTypes.includes(type)) {
                return `Unsupported shape type: ${JSON.stringify(objData.type ?? null)}`;
            }
            if (objData.userData !== undefined && (objData.userData === null || typeof objData.userData !== 'object' || Array.isArray(objData.userData))) {
                return 'Invalid userData for object';
            }
            const transformError = this._validateTransform(objData.transform);
            if (transformError) {
                return transformError;
            }
            if (objData.material !== undefined && objData.material !== null && typeof objData.material !== 'object') {
                return 'Invalid material for object';
            }
        }
        
        for (const groupData of data.groups || []) {
            if (!groupData || typeof groupData !== 'object' || !groupData.id) {
                return 'Invalid group entry';
            }
            const transformError = this._validateTransform(groupData.transform);
            if (transformError) {
                return transformError;
            }
        }
        
        return null;
    }
    
    /**
     * Validate a transform block (position/rotation/scale arrays)
     */
    _validateTransform(transform) {
        if (transform === undefined || transform === null) return null;
        if (typeof transform !== 'object') return 'Invalid transform';
        
        const check = (arr, label, minLen) => {
            if (arr === undefined) return null;
            if (!Array.isArray(arr) || arr.length < minLen || !arr.every(n => Number.isFinite(n))) {
                return `Invalid ${label} in transform`;
            }
            return null;
        };
        
        return check(transform.position, 'position', 3) ||
               check(transform.rotation, 'rotation', 4) ||
               check(transform.scale, 'scale', 3);
    }
    
    /**
     * Copy plain JSON-safe userData, blocking prototype-pollution keys (F01/F03)
     */
    _sanitizeUserData(userData) {
        const clean = {};
        if (!userData || typeof userData !== 'object') return clean;
        for (const key of Object.keys(userData)) {
            if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
            const value = userData[key];
            const t = typeof value;
            if (t === 'string' || t === 'number' || t === 'boolean' || value === null) {
                clean[key] = value;
            } else if (Array.isArray(value) && value.every(v => ['string', 'number', 'boolean'].includes(typeof v) || v === null)) {
                clean[key] = [...value];
            }
        }
        return clean;
    }
    
    /**
     * Reconstruct a single object from import data without touching the scene
     * @returns {THREE.Object3D|null} The created mesh or null on failure
     */
    _reconstructObject(objData) {
        const userData = this._sanitizeUserData(objData.userData);
        
        // Reconstruct options from userData
        const options = {
            color: userData.color,
            skipStateCapture: true
        };
        
        // Map specific properties based on type
        if (objData.type === 'text') {
            options.text = userData.textContent;
            options.font = userData.textFont;
            options.height = userData.textHeight;
            options.bevel = userData.textBevel;
        } else if (objData.type === 'polygon') {
            options.sides = userData.polygonSides;
        } else if (objData.type === 'tube') {
            options.radius = userData.outerRadius;
            options.holeRadius = userData.holeRadius;
            options.length = userData.length;
        } else if (objData.type === 'box' && userData.boxSize) {
            options.width = userData.boxSize[0];
            options.height = userData.boxSize[1];
            options.depth = userData.boxSize[2];
        } else if (objData.type === 'sphere' && userData.sphereRadius != null) {
            options.radius = userData.sphereRadius;
        }
        
        return this.swk.shapeFactory.createShape(objData.type, options);
    }
    
    /**
     * Import scene from JSON.
     * Fully validates and reconstructs before committing; on any failure the
     * current scene, selection, and history are left untouched (F03).
     * @param {string|Object} jsonContent - JSON string or object
     * @returns {Promise<boolean>} Success status
     */
    async importJSON(jsonContent) {
        let staged = [];
        
        try {
            const data = typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent;
            
            // Validate the complete document before touching the scene
            const validationError = this._validateImportDocument(data);
            if (validationError) {
                throw new Error(validationError);
            }
            
            // Stage phase: reconstruct every object without mutating the scene
            for (const objData of data.objects) {
                const mesh = this._reconstructObject(objData);
                if (!mesh) {
                    throw new Error(
                        `Failed to reconstruct object of type "${objData.type}" ` +
                        `(fonts may not be loaded yet for text shapes)`
                    );
                }
                staged.push({ mesh, objData });
            }
            
            // Commit phase: only reached when every object reconstructed
            this.swk.clearScene(true);
            
            // Restore groups
            (data.groups || []).forEach(groupData => {
                this.swk.groupManager.restoreGroup(groupData);
            });
            
            // Add staged objects to the scene
            staged.forEach(({ mesh, objData }) => {
                this.swk.sceneManager.add(mesh);
                this.swk.objects.push(mesh);
                this.swk.emit('objectAdded', mesh);
                
                // Restore transform
                if (objData.transform) {
                    mesh.position.fromArray(objData.transform.position);
                    mesh.quaternion.fromArray(objData.transform.rotation);
                    mesh.scale.fromArray(objData.transform.scale);
                }
                
                // Restore name (as plain data; UI renders it as text, F01)
                mesh.name = typeof objData.name === 'string' ? objData.name : mesh.name;
                
                // Restore visibility (F06)
                if (typeof objData.visible === 'boolean') {
                    mesh.visible = objData.visible;
                }
                
                // Restore material state (F06)
                if (objData.material && mesh.material) {
                    const m = objData.material;
                    if (Number.isFinite(m.color)) {
                        mesh.material.color.setHex(m.color);
                        mesh.userData.color = m.color;
                    }
                    if (Number.isFinite(m.opacity)) mesh.material.opacity = m.opacity;
                    if (typeof m.transparent === 'boolean') mesh.material.transparent = m.transparent;
                    if (typeof m.wireframe === 'boolean') mesh.material.wireframe = m.wireframe;
                }
                
                // Restore sanitized userData
                Object.assign(mesh.userData, this._sanitizeUserData(objData.userData));
                
                // Handle grouping
                if (objData.groupId) {
                    this.swk.groupManager.restoreObjectToGroup(mesh, objData.groupId);
                }
            });
            
            // Capture state for the import action
            this.swk.captureState('Import JSON');
            
            console.log(`Imported ${data.objects.length} objects from JSON`);
            return true;
            
        } catch (error) {
            // Dispose any staged objects so the failed import leaks nothing
            staged.forEach(({ mesh }) => {
                if (mesh.geometry) mesh.geometry.dispose();
                if (mesh.material) {
                    if (Array.isArray(mesh.material)) mesh.material.forEach(m => m.dispose());
                    else mesh.material.dispose();
                }
            });
            staged = [];
            
            console.error('JSON import error:', error);
            this.emit('importError', { error: error.message });
            return false;
        }
    }

    /**
     * Clean up
     */
    dispose() {
        this.removeAllListeners();
    }
}

export default ExportManager;
